import { useState, useEffect, useCallback, useRef } from "react";
import type { Socket } from "socket.io-client";
import {
  createSocket,
  sendMessage as socketSendMessage,
  disconnectSocket,
  type SocketCallbacks,
} from "@/lib/socket";
import {
  MessageType,
  type ChatMessage,
  type ConnectedPayload,
  type SessionCreatedPayload,
  type SessionModeChangedPayload,
  type ErrorPayload,
  type MessageHistoryResponse,
  type AssistantStreamStartPayload,
  type AssistantStreamChunkPayload,
  type AssistantStreamEndPayload,
} from "shared";

// Server URL - use environment variable or default
const SERVER_URL = import.meta.env.VITE_SERVER_URL || "http://localhost:3002";

// Storage key prefix
const STORAGE_KEY_PREFIX = "smallforce_chat_";

interface UseChatOptions {
  assistantId: string;
  userId?: string; // Optional, will be generated if not provided
}

interface UseChatReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isConnecting: boolean;
  isRefreshing: boolean;
  isStreaming: boolean;
  streamingMessageId: string | null;
  sessionId: string | null;
  isPendingSession: boolean;
  mode: "AUTO" | "MANUAL";
  error: string | null;
  sendMessage: (content: string) => void;
  loadHistory: () => Promise<void>;
  resetSession: () => void;
}

/**
 * Get or create user ID from localStorage
 */
function getOrCreateUserId(providedUserId?: string): string {
  const storageKey = `${STORAGE_KEY_PREFIX}user_id`;

  if (providedUserId) {
    localStorage.setItem(storageKey, providedUserId);
    return providedUserId;
  }

  const storedUserId = localStorage.getItem(storageKey);
  if (storedUserId) {
    return storedUserId;
  }

  const newUserId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  localStorage.setItem(storageKey, newUserId);
  return newUserId;
}

/**
 * Get stored session ID for an assistant (if exists and not pending)
 */
function getStoredSessionId(assistantId: string): string | null {
  const storageKey = `${STORAGE_KEY_PREFIX}session_${assistantId}`;
  return localStorage.getItem(storageKey);
}

/**
 * Store session ID for an assistant
 */
function storeSessionId(assistantId: string, sessionId: string): void {
  const storageKey = `${STORAGE_KEY_PREFIX}session_${assistantId}`;
  localStorage.setItem(storageKey, sessionId);
}

/**
 * Custom hook for chat functionality
 * Handles socket connection, messages, and history
 */
export function useChat({
  assistantId,
  userId,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isPendingSession, setIsPendingSession] = useState(true);
  const [mode, setMode] = useState<"AUTO" | "MANUAL">("AUTO");
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const userIdRef = useRef<string>(getOrCreateUserId(userId));
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );

  // Fetch token on mount
  useEffect(() => {
    async function fetchToken() {
      setIsConnecting(true);
      setError(null);

      try {
        // Check for stored session ID (resume existing session)
        const storedSessionId = getStoredSessionId(assistantId);

        const response = await fetch(`${SERVER_URL}/api/auth/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            assistant_id: assistantId,
            user_id: userIdRef.current,
            // If we have a stored session, request that session
            session_id: storedSessionId || undefined,
          }),
        });

        if (!response.ok) {
          // Parse error response to provide specific error messages
          let errorMessage = "Failed to initialize chat";
          try {
            const errorData = await response.json();
            if (response.status === 404) {
              errorMessage = errorData.error || "Assistant not found";
            } else if (response.status === 403) {
              errorMessage =
                errorData.error ||
                "This assistant is not available for public chat";
            } else if (response.status === 500) {
              errorMessage = "Server error - please try again later";
            } else {
              errorMessage = errorData.error || "Failed to initialize chat";
            }
          } catch {
            // If parsing fails, use status-based messages
            if (response.status === 404) {
              errorMessage = "Assistant not found";
            } else if (response.status === 403) {
              errorMessage = "This assistant is not available for public chat";
            } else if (response.status >= 500) {
              errorMessage = "Server error - please try again later";
            }
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setToken(data.token);
        setSessionId(data.session_id);
        setIsPendingSession(data.is_pending_session ?? true);

        // If we got a real session back, store it
        if (!data.is_pending_session && data.session_id) {
          storeSessionId(assistantId, data.session_id);
        }
      } catch (err) {
        console.error("Token fetch error:", err);
        setError(
          err instanceof Error ? err.message : "Failed to initialize chat",
        );
        setIsConnecting(false);
      }
    }

    fetchToken();
  }, [assistantId]);

  // Connect socket when token is available
  useEffect(() => {
    if (!token) return;

    const callbacks: SocketCallbacks = {
      onConnected: (payload: ConnectedPayload) => {
        setIsConnected(true);
        setIsConnecting(false);
        setSessionId(payload.session_id);
        setIsPendingSession(payload.is_pending_session);
        setMode(payload.mode);
      },
      onMessage: (message: ChatMessage) => {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      },
      onSessionCreated: (payload: SessionCreatedPayload) => {
        // Session was created, store the new session ID
        setSessionId(payload.session_id);
        setIsPendingSession(false);
        storeSessionId(assistantId, payload.session_id);
        console.log("[useChat] Session created:", payload.session_id);
      },
      onSessionModeChanged: (payload: SessionModeChangedPayload) => {
        setMode(payload.mode);
      },
      onError: (errorPayload: ErrorPayload) => {
        console.error("Socket error:", errorPayload);

        // Handle credits exhausted gracefully as a chat message, not a crash
        if (errorPayload.type === "CREDITS_EXHAUSTED") {
          const creditMessage: ChatMessage = {
            id: `credits-error-${Date.now()}`,
            type: MessageType.ASSISTANT_MESSAGE,
            session_id: sessionId || "",
            content:
              "I'm sorry, but this assistant has run out of credits and can't respond right now. Please contact support for assistance.",
            sender_id: "system",
            created_at: new Date().toISOString(),
            metadata: { isSystemMessage: true },
          };
          setMessages((prev) => [...prev, creditMessage]);
          return;
        }

        setError(errorPayload.message);
      },
      onDisconnect: () => {
        setIsConnected(false);
      },
      onReconnect: () => {
        setIsConnected(true);
      },
      // Streaming callbacks
      onStreamStart: (payload: AssistantStreamStartPayload) => {
        setIsStreaming(true);
        setStreamingMessageId(payload.message_id);

        // Add a placeholder message for the streaming response
        const placeholderMessage: ChatMessage = {
          id: payload.message_id,
          type: MessageType.ASSISTANT_MESSAGE,
          session_id: payload.session_id,
          content: "",
          sender_id: "assistant",
          created_at: new Date().toISOString(),
          metadata: { isStreaming: true },
        };
        setMessages((prev) => [...prev, placeholderMessage]);
      },
      onStreamChunk: (payload: AssistantStreamChunkPayload) => {
        // Update the streaming message with accumulated content
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === payload.message_id
              ? { ...msg, content: payload.accumulated }
              : msg,
          ),
        );
      },
      onStreamEnd: (payload: AssistantStreamEndPayload) => {
        setIsStreaming(false);
        setStreamingMessageId(null);

        // Update the message with final content and remove streaming flag
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === payload.message_id
              ? {
                  ...msg,
                  content: payload.final_content,
                  created_at: payload.created_at,
                  metadata: { ...msg.metadata, isStreaming: false },
                }
              : msg,
          ),
        );
      },
    };

    const socket = createSocket(SERVER_URL, token, callbacks);
    socketRef.current = socket;

    return () => {
      disconnectSocket(socket);
      socketRef.current = null;
    };
  }, [token, assistantId]);

  // Send message
  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current || !content.trim()) return;
    socketSendMessage(socketRef.current, content, "USER_MESSAGE");
  }, []);

  // Load message history
  const loadHistory = useCallback(async () => {
    // Don't load history for pending sessions (they don't exist yet)
    if (!sessionId || !token || isPendingSession) {
      console.log("[useChat] Skipping history load:", {
        sessionId,
        hasToken: !!token,
        isPendingSession,
      });
      return;
    }

    try {
      console.log("[useChat] Loading history for session:", sessionId);
      const response = await fetch(
        `${SERVER_URL}/api/sessions/${sessionId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        // Session might not exist anymore, clear stored session
        if (response.status === 404) {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}session_${sessionId}`);
        }
        throw new Error("Failed to load history");
      }

      const data: MessageHistoryResponse = await response.json();
      setMessages(data.messages as unknown as ChatMessage[]);
    } catch (err) {
      console.error("History load error:", err);
    }
  }, [sessionId, token, isPendingSession]);

  // Load history when session is ready (only for non-pending sessions)
  useEffect(() => {
    if (sessionId && token && isConnected && !isPendingSession) {
      loadHistory();
    }
  }, [sessionId, token, isConnected, isPendingSession, loadHistory]);

  // Reset session (clear stored session and reload page)
  const resetSession = useCallback(() => {
    // Clear stored session for this assistant
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}session_${assistantId}`);
    // Clear messages
    setMessages([]);
    // Reload the page to get a fresh session
    window.location.reload();
  }, [assistantId]);

  // Track refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Wrapped loadHistory with refreshing state
  const refreshMessages = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadHistory();
    } finally {
      setIsRefreshing(false);
    }
  }, [loadHistory]);

  return {
    messages,
    isConnected,
    isConnecting,
    isRefreshing,
    isStreaming,
    streamingMessageId,
    sessionId,
    isPendingSession,
    mode,
    error,
    sendMessage,
    loadHistory: refreshMessages,
    resetSession,
  };
}
